import * as React from 'react';
import { Element } from 'react-scroll';

import { Box, Notification, SizeName } from '@alinta-digital/asg';
import { StateType, ErrorType } from '../../types';
import { getContactNumber, pushAnalytics } from '../../utils/utils';
import { StyledLink } from '../../Common.Styled';

interface ErrorNotificationProps {
  margin?:
    | SizeName
    | {
        bottom?: SizeName | string;
        horizontal?: SizeName | string;
        left?: SizeName | string;
        right?: SizeName | string;
        top?: SizeName | string;
        vertical?: SizeName | string;
      }
    | string;
  errorType: ErrorType;
  onClick?: () => void;
  stateShortName: StateType;
}

export const ErrorNotification = (props: ErrorNotificationProps) => {
  const { margin, errorType, onClick, stateShortName } = props;
  let errorContent;
  let formErrorWarnName;
  const contactNumber = getContactNumber(stateShortName);

  React.useEffect(() => {
    pushAnalytics('load.0.directdebitsetupfailure');
  }, []);
  switch (errorType) {
    case 'Credit card limit':
      formErrorWarnName = 'creditCardError';
      errorContent = (
        <>
          To apply a Direct Debit using a credit/debit card via our online
          self-service function you must ensure you&apos;ve only selected 1
          electricity and 1 gas account. To apply a Direct Debit to multiple
          accounts, you&apos;ll need to switch to either Bank transfer (using
          your bank account details) or contact us on&nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-tel-linkButton`}
            data-testid={`${formErrorWarnName}-tel-linkButton`}
            color="grey-9"
            size="xsmall"
            href={`tel:${contactNumber}`}
          >
            {contactNumber}
          </StyledLink>
          &nbsp;and select option 4 where we can assist you over the phone to
          set up direct debit via credit/debit card for multiple accounts.
        </>
      );
      break;
    case 'Service Limit':
      formErrorWarnName = 'addressError';
      errorContent = (
        <>
          Unfortunately, we&apos;re unable to process your request. Please
          contact us on &nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-tel-linkButton`}
            data-testid={`${formErrorWarnName}-tel-linkButton`}
            color="grey-9"
            secondaryColor="grey-9"
            size="xsmall"
            href={`tel:${contactNumber}`}
          >
            {contactNumber}
          </StyledLink>
          &nbsp; and select option 4.
        </>
      );
      break;
    case 'Locked Out':
      formErrorWarnName = 'locked';
      errorContent = (
        <>
          Your account is locked due to 3 invalid logins. Please&nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-linkButton`}
            data-testid={`${formErrorWarnName}-linkButton`}
            onClick={onClick}
            color="grey-9"
            secondaryColor="grey-9"
            size="xsmall"
          >
            try again
          </StyledLink>
          &nbsp;in 48 hours or call&nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-tel-linkButton`}
            data-testid={`${formErrorWarnName}-tel-linkButton`}
            color="grey-9"
            secondaryColor="grey-9"
            size="xsmall"
            href={`tel:${contactNumber}`}
          >
            {contactNumber}
          </StyledLink>
          &nbsp;Monday to Friday 8 am to 9 pm, or Saturday 9 am to 6 pm
        </>
      );
      break;
    case 'Bad Request':
    case 'Internal Server Error':
    case 'Bad Gateway':
      formErrorWarnName = 'dataError';
      errorContent = (
        <>
          Oops! Something went wrong. Please
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-linkButton`}
            data-testid={`${formErrorWarnName}-linkButton`}
            onClick={onClick}
            color="grey-9"
            secondaryColor="grey-9"
            size="xsmall"
          >
            try again
          </StyledLink>
          later
        </>
      );
      break;
    case 'Invalid account or customer details':
      formErrorWarnName = 'invalidCredentials';
      errorContent = (
        <>
          Please check your details and&nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-linkButton`}
            data-testid={`${formErrorWarnName}-linkButton`}
            onClick={onClick}
            color="grey-9"
            size="xsmall"
          >
            try again
          </StyledLink>
        </>
      );
      break;
    case 'Submission error':
      formErrorWarnName = 'submission';
      errorContent = (
        <>
          Oops! Something went wrong. Please &nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-linkButton`}
            data-testid={`${formErrorWarnName}-linkButton`}
            onClick={onClick}
            color="grey-9"
            size="xsmall"
          >
            try again
          </StyledLink>{' '}
          &nbsp; or speak with one of our team, simply call&nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-tel-linkButton`}
            data-testid={`${formErrorWarnName}-tel-linkButton`}
            color="grey-9"
            size="xsmall"
            href={`tel:${contactNumber}`}
          >
            {contactNumber}
          </StyledLink>
          , with your customer number handy. We&apos;re available Monday to
          Friday from 8am to 9pm and Saturdays from 9am to 6pm (AEST).
        </>
      );
      break;
    case 'WA Unauthorised':
      formErrorWarnName = 'unauthorisedWA';
      errorContent = (
        <>
          Unfortunately, we&apos;re unable to process your request. Please
          contact us on &nbsp;
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-tel-linkButton`}
            data-testid={`${formErrorWarnName}-tel-linkButton`}
            color="grey-9"
            secondaryColor="grey-9"
            size="xsmall"
            href={`tel:${getContactNumber('WA')}`}
          >
            {getContactNumber('WA')}
          </StyledLink>
          &nbsp; and select option 3.
        </>
      );
      break;
    case 'Other':
    default:
      formErrorWarnName = 'otherError';
      errorContent = (
        <>
          Something went wrong, please{' '}
          <StyledLink
            type="button"
            id={`${formErrorWarnName}-linkButton`}
            data-testid={`${formErrorWarnName}-linkButton`}
            onClick={onClick}
            color="grey-9"
            secondaryColor="grey-9"
            size="xsmall"
          >
            try again
          </StyledLink>
          .
        </>
      );
      break;
  }

  return (
    <Box width="100%" margin={margin}>
      <Element name="notificationError" id={`${formErrorWarnName}-element`} />
      <Notification
        id={formErrorWarnName}
        data-testid={`${formErrorWarnName}-notificationError`}
        type="warning"
        message={errorContent}
      />
    </Box>
  );
};
